import { createSimpleStore } from "@expert/state-utils";

import type { C360Interaction, C360InteractionCreator } from "../api/interactions";

export interface C360RecordedInteraction extends C360Interaction {
    /** Missing on a session saved by a build from before this field: then only our line is closed at the end. */
    createdBy?: C360InteractionCreator;
}

interface C360InteractionState {
    /** The C360 interaction recording each session, keyed by session ID. */
    sessions: Record<string, C360RecordedInteraction>;
}

interface C360InteractionActions {
    getInteraction: (sessionId: string) => C360RecordedInteraction | undefined;
    setInteraction: (sessionId: string, interaction: C360RecordedInteraction) => void;
    removeSession: (sessionId: string) => void;
}

type C360InteractionStore = C360InteractionState & C360InteractionActions;

export const useC360InteractionStore = createSimpleStore<C360InteractionStore>({
    devtools: {
        store: "c360Interaction",
        name: "workspace/c360-interaction",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
    // Saved like the session store, so a page refresh mid-call still closes the line when the session ends.
    persist: { name: "c360-interaction-store", partialize: (state) => ({ sessions: state.sessions }) },
})((set, get) => ({
    sessions: {},
    getInteraction: (sessionId) => get().sessions[sessionId],
    setInteraction: (sessionId, interaction) =>
        set((state) => ({ sessions: { ...state.sessions, [sessionId]: interaction } }), false, "setInteraction"),
    removeSession: (sessionId) =>
        set(
            (state) => {
                const sessions = { ...state.sessions };
                delete sessions[sessionId];
                return { sessions };
            },
            false,
            "removeSession",
        ),
}));
